import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Typography, Button, Row, Col, Space, Spin, Empty, Tabs, message, Descriptions } from 'antd';
import { ArrowLeftOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { getEmployeeById, deleteEmployee } from '@/api/employees';
import type { Employee } from '@/types/resources/employee';
import type { Money } from '@/types/common';
import { EmployeeModal } from '@/components/features/resources/employees/EmployeeModal';
import ConfirmDeleteModal from '@/components/modal/ConfirmDeleteModal';
import { useAuth } from '@/contexts/AuthContext';
import ContactsList from '@/components/common/ContactsList';
import ActivityLogList from '@/components/common/ActivityLogList';
import { UserInfoTab, InvitationsTab } from '@/components/tab';

const { Title } = Typography;

const EmployeeDetails: React.FC = () => {
  const { t } = useTranslation(['employees', 'common']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const state = location.state as { employee: Employee; from: string } | null;
  const [activeTabKey, setActiveTabKey] = useState('overview');
  const { firestoreUser } = useAuth();

  // State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const shouldFetch = !state?.employee && !!id && !!firestoreUser;

  // Queries
  const { data: employee, isLoading } = useQuery({
    queryKey: ['employee', id],
    queryFn: () => {
      if (!id || !firestoreUser) return null;
      return getEmployeeById(id, firestoreUser);
    },
    enabled: shouldFetch
  });

  // Mutations
  const deleteMutation = useMutation({
    mutationFn: async (employeeId: string) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return deleteEmployee(employeeId, firestoreUser);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      message.success(t('employees:messages.deleteSuccess'));
      navigate('/resources/employees');
    },
    onError: (error: Error) => {
      message.error(error.message || t('employees:messages.deleteFailed'));
    }
  });

  // Use state data if available, otherwise use fetched data
  const displayEmployee = state?.employee || employee;

  // Loading state - only show if no state data is available
  if (isLoading && !state?.employee) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!displayEmployee) {
    return <Card>{t('employees:notFound')}</Card>;
  }

  const handleEdit = () => {
    setIsModalOpen(true);
  };

  const handleDeleteClick = () => {
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    if (id) {
      deleteMutation.mutate(id);
      setOpenDeleteDialog(false);
    }
  };

  const handleCancelDelete = () => {
    setOpenDeleteDialog(false);
  };

  const handleModalSuccess = () => {
    setIsModalOpen(false);
    queryClient.invalidateQueries({ queryKey: ['employees', id] });
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
  };

  const formatMoney = (amount: Money) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: amount.currency || 'INR'
    }).format(amount.amount);
  };

  const tabItems = [
    {
      key: 'overview',
      label: t('employees:common.details'),
      children: (
        <Card>
          <Descriptions bordered column={1}>
            <Descriptions.Item label={t('employees:fields.employeeId')}>
              {displayEmployee.employeeId}
            </Descriptions.Item>
            <Descriptions.Item label={t('employees:fields.department')}>
              {t(`employees:departments.${displayEmployee.department}`)}
            </Descriptions.Item>
            <Descriptions.Item label={t('employees:fields.role')}>
              {t(`employees:roles.${displayEmployee.role}`)}
            </Descriptions.Item>
            <Descriptions.Item label={t('employees:fields.status')}>
              {t(`employees:status.${displayEmployee.status}`)}
            </Descriptions.Item>
            <Descriptions.Item label={t('employees:fields.joiningDate')}>
              {displayEmployee.joiningDate ? new Date(displayEmployee.joiningDate).toLocaleDateString() : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('employees:fields.exitDate')}>
              {displayEmployee.exitDate ? new Date(displayEmployee.exitDate).toLocaleDateString() : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('employees:fields.salary')}>
              {displayEmployee.salary ? formatMoney(displayEmployee.salary) : '-'}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      ),
    },
    {
      key: 'user',
      label: t('employees:common.userInfo', 'User Info'),
      children: displayEmployee?.user?.id ? (
        <UserInfoTab userId={displayEmployee.user.id} />
      ) : <Empty description={t('employees:notFound')} />,
    },
    {
      key: 'invitations',
      label: t('employees:common.invitations', 'Invitations'),
      children: displayEmployee?.user?.id ? (
        <InvitationsTab userId={displayEmployee.user.id} userType="employee" />
      ) : <Empty description={t('employees:notFound')} />,
    },
    {
      key: 'contacts',
      label: t('employees:fields.contacts'),
      children: <ContactsList contacts={displayEmployee?.contacts || []} />,
    },
    {
      key: 'activityLog',
      label: t('employees:common.activityLog'),
      children: <ActivityLogList activityRecords={displayEmployee?.activityRecords || []} />,
    },
  ];

  return (
    <div style={{ width: '100%' }}>
      {/* Header */}
      <Row justify="space-between" align="middle" gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
              {t('common:back')}
            </Button>
            <Title level={3} style={{ margin: 0 }}>{displayEmployee.user?.displayName || displayEmployee.employeeId}</Title>
          </Space>
        </Col>
        <Col>
          <Space wrap>
            <Button icon={<EditOutlined />} onClick={handleEdit}>
              {t('common:actions.edit')}
            </Button>
            <Button danger icon={<DeleteOutlined />} onClick={handleDeleteClick}>
              {t('common:actions.delete')}
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Content */}
      <Card>
        <Tabs
          activeKey={activeTabKey}
          onChange={setActiveTabKey}
          items={tabItems}
        />
      </Card>

      <EmployeeModal
        open={isModalOpen}
        employee={displayEmployee}
        onSuccess={handleModalSuccess}
        onCancel={handleModalCancel}
      />

      <ConfirmDeleteModal
        open={openDeleteDialog}
        title={t('common:confirmDeleteTitle')}
        description={t('common:confirmDelete')}
        onOk={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
};

export default EmployeeDetails; 